import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { SocialMediaLink, SocialMediaLinkFormProps } from './SocialMediaLinks.d'
import { Card } from 'primereact/card'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../common/forms/fields/TextField'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import FormActions from '../../../common/forms/FormActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'

const SocialMediaLinkForm: FC<SocialMediaLinkFormProps> = ({
    inPopup = true,
    config = {},
}) => {
    const theme = useAppSelector((state) => state.theme.theme)

    const navigate = useNavigate()
    const axiosPrivate = useAxiosPrivate()

    const emptySocialMediaLink: SocialMediaLink = {}

    const SocialMediaLinkFormSchema = Yup.object().shape({})

    const [socialMediaLinkDialog, setSocialMediaLinkDialog] = useState(true)
    const [socialMediaLink, setSocialMediaLink] =
        useState<SocialMediaLink>(emptySocialMediaLink)
    const [availableSocialMedia, setAvailableSocialMedia] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/administration/social-media-links')
                .finally(() => {
                    setIsLoading(false)
                })
            if (response.data?.success === true) {
                if (response.data.data.social_media) {
                    let _socialMedia = {}
                    for (const i in response.data.data.social_media) {
                        _socialMedia[
                            'social-media-' +
                                response.data.data.social_media[i]['code']
                        ] = response.data.data.social_media[i]['url']
                    }
                    setSocialMediaLink(_socialMedia)
                    setAvailableSocialMedia(
                        response.data.data.available_social_media
                    )
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load social media links')
            }
        }
    }, [axiosPrivate])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record) => {
        setErrMsg('')
        setIsSubmitting(true)

        delete record['social_media']

        try {
            const response = await axiosPrivate
                .post('/administration/social-media-links/update', record, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                if (inPopup) hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err) {
            handleAxiosErrors(err, setErrMsg, setErrorFields, config)
        }
    }

    const hideDialog = () => {
        setSocialMediaLinkDialog(false)
        navigate('/administration')
    }

    const FormFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'socialMediaLink-form-submit-btn'
                    )
                    formSubmitGtn.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={{
                    ...socialMediaLink,
                }}
                validationSchema={SocialMediaLinkFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    return (
                        <FormikForm>
                            <fieldset>
                                <legend>View/Update</legend>
                                <Row>
                                    {availableSocialMedia.map(
                                        (socialMediaItem: any) => {
                                            return (
                                                <Col
                                                    key={socialMediaItem.id}
                                                    xl={6}
                                                >
                                                    <TextField
                                                        id={`formTeamMember${socialMediaItem.code}Url`}
                                                        name={`social-media-${socialMediaItem.code}`}
                                                        label={`${socialMediaItem.name}`}
                                                        placeholder={`Enter ${socialMediaItem.name} Url`}
                                                        formikProps={
                                                            formikProps
                                                        }
                                                    />
                                                </Col>
                                            )
                                        }
                                    )}
                                </Row>
                            </fieldset>
                            <input
                                type="submit"
                                id="socialMediaLink-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={socialMediaLinkDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '50vw' }}
                        header={'Edit social media links'}
                        modal
                        className="p-fluid"
                        footer={FormFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    {FormContent}
                    <FormActions formFooter={FormFooter} />
                </>
            )}
        </>
    )
}

export default SocialMediaLinkForm
